/**
 * NPC Behavior Adapter
 * 
 * Bridges the new NPCBehavior hook system with the existing NPCAIBehavior interface.
 * Creates the API objects and delegates to the appropriate hooks.
 */

import { Vector3 } from '@babylonjs/core';
import type { NPCController } from '../systems/NPCController';
import type { NPCAIBehavior } from '../systems/NPCController';
import type { NPCBehavior } from './behaviors/NPCBehavior';
import type { AITickAPI, DetectionAPI, DamageAPI, DeathAPI, SpawnAPI, NPCGameServices } from './hooks/NPCHookAPI';
import { EntityType } from '../types/EntityTypes';

/**
 * Detection state tracking for player proximity
 */
interface DetectionState {
  wasPlayerDetected: boolean;
  lastPlayerDistance: number;
  lastPlayerT: number;
}

/**
 * Adapter that implements NPCAIBehavior using the new hook-based NPCBehavior system
 */
export class BehaviorAdapter implements NPCAIBehavior {
  private behavior: NPCBehavior;
  private detectionState: DetectionState = {
    wasPlayerDetected: false,
    lastPlayerDistance: Infinity,
    lastPlayerT: 0,
  };
  
  // Services - stubbed for now
  private services: NPCGameServices;
  
  // Detection range (can be configured per-behavior)
  private detectionRange: number;
  
  constructor(
    behavior: NPCBehavior, 
    services?: Partial<NPCGameServices>,
    detectionRange: number = 10.0
  ) {
    this.behavior = behavior;
    this.detectionRange = detectionRange;
    
    // Create services with defaults/stubs
    this.services = {
      audio: services?.audio ?? {
        play: (_soundId: string, _options?: { volume?: number; position?: Vector3 }) => { /* stub */ },
      },
      particles: services?.particles ?? {
        spawn: (_preset: string, _position: Vector3, _options?: { count?: number; scale?: number }) => { /* stub */ },
      },
      events: services?.events ?? {
        emit: (_eventType: string, _payload?: Record<string, unknown>) => { /* stub */ },
      },
      spawner: services?.spawner ?? {
        spawn: (_entityId: string, _position: Vector3) => { /* stub */ },
      },
    };
  }
  
  /**
   * Main update called by NPCController every frame
   */
  update(deltaTime: number, npc: NPCController): void {
    // Calculate player distance (approximation - we don't have direct player ref)
    const playerDistance = this.detectionState.lastPlayerDistance;
    
    // Build the API object for this tick
    const tickAPI = this.createTickAPI(npc, deltaTime, playerDistance);
    
    // Check for detection state changes
    this.updateDetection(npc, playerDistance);
    
    // Call the onTick hook
    if (this.behavior.onTick) {
      this.behavior.onTick(tickAPI);
    }
  }
  
  /**
   * Called when NPC spawns
   */
  onSpawn(npc: NPCController): void {
    if (this.behavior.onSpawn) {
      const api = this.createSpawnAPI(npc);
      this.behavior.onSpawn(api);
    }
  }
  
  /**
   * Called when NPC takes damage
   * @returns true if damage should be applied, false to cancel
   */
  onDamage(npc: NPCController, damage: number, attackerId: string): boolean {
    if (this.behavior.onDamage) {
      const api = this.createDamageAPI(npc, damage, attackerId);
      this.behavior.onDamage(api);
      return !api.cancelled;
    }
    return true;
  }
  
  /**
   * Called when NPC dies
   */
  onDeath(npc: NPCController, killerId: string): void {
    if (this.behavior.onDeath) {
      const api = this.createDeathAPI(npc, killerId);
      this.behavior.onDeath(api);
    }
  }
  
  /**
   * Get the behavior ID
   */
  getBehaviorId(): string {
    return this.behavior.id;
  }
  
  // === Private helpers ===
  
  private createTickAPI(npc: NPCController, deltaTime: number, playerDistance: number): AITickAPI {
    const hp = npc.getHp();
    const npcT = npc.playerT;
    const playerT = this.detectionState.lastPlayerT;
    // Direction: +1 if player is to the right (higher T), -1 if player is to the left (lower T)
    const playerDirection = playerT > npcT ? 1 : playerT < npcT ? -1 : 0;
    
    return {
      npc,
      deltaTime,
      playerDistance,
      playerT,
      playerDirection,
      health: hp.current,
      maxHealth: hp.max,
      npcT,
      playerInRange: playerDistance <= this.detectionRange,
      combatCooldownReady: true, // TODO: Get from combat system
      
      actions: {
        setMovement: (direction: number) => npc.setMovementInput(direction),
        setJump: (pressed: boolean) => npc.setJumpInput(pressed),
        setAttack: (pressed: boolean) => npc.setAttackInput(pressed),
        setSpeed: (_multiplier: number) => { /* TODO: implement speed control */ },
        playAnimation: (_name: string) => { /* TODO: trigger animation */ },
        setFacing: (direction: number) => npc.setFacingDirection(direction),
      },
      
      services: this.services,
    };
  }
  
  private createDetectionAPI(npc: NPCController, playerDistance: number): DetectionAPI {
    return {
      npc,
      player: { 
        id: 'player', 
        type: EntityType.CHARACTER, 
        displayName: 'Player', 
        physicsBodyId: 'player',
        isPlayer: true,
        mesh: null as any 
      },
      distance: playerDistance,
      direction: new Vector3(0, 0, 1), // TODO: Calculate actual direction
      
      actions: {
        alert: () => { /* TODO: Play alert animation/sound */ },
        chase: () => { /* TODO: Switch to chase mode */ },
        flee: () => { /* TODO: Switch to flee mode */ },
        ignore: () => { /* Do nothing */ },
        switchMovement: (_mode: 'patrol' | 'follow' | 'idle') => { /* TODO: Switch movement */ },
      },
      
      services: this.services,
    };
  }
  
  private createDamageAPI(npc: NPCController, damage: number, attackerId: string): DamageAPI {
    let cancelled = false;
    const hp = npc.getHp();
    
    return {
      npc,
      source: { 
        id: attackerId, 
        type: EntityType.CHARACTER, 
        displayName: attackerId, 
        physicsBodyId: attackerId,
        isPlayer: false,
        mesh: null as any 
      },
      amount: damage,
      damageType: 'melee',
      impactPoint: null,
      currentHealth: hp.current,
      maxHealth: hp.max,
      cancelled,
      
      actions: {
        cancel: () => { cancelled = true; },
        flash: () => { /* TODO: Flash mesh */ },
        knockback: (_force: number) => { /* TODO: Apply knockback */ },
        playSound: (_id: string) => { /* TODO: Play sound */ },
      },
      
      services: this.services,
    };
  }
  
  private createDeathAPI(npc: NPCController, killerId: string): DeathAPI {
    return {
      npc,
      killer: { 
        id: killerId, 
        type: EntityType.CHARACTER, 
        displayName: killerId, 
        physicsBodyId: killerId,
        isPlayer: false,
        mesh: null as any 
      },
      cause: 'damage',
      position: npc.character.position.clone(),
      
      actions: {
        remove: () => { /* TODO: Remove NPC from world */ },
        respawn: (_delaySeconds: number) => { /* TODO: Schedule respawn */ },
        spawnLoot: (_tableId: string) => { /* TODO: Spawn loot */ },
        playAnimation: (_name: string) => { /* TODO: Play animation */ },
        delay: (_seconds: number, _callback: () => void) => { /* TODO: Schedule callback */ },
      },
      
      services: this.services,
    };
  }
  
  private createSpawnAPI(npc: NPCController): SpawnAPI {
    return {
      npc,
      position: npc.character.position.clone(),
      spawnT: npc.playerT,
      splineLength: npc.getSplineLength(),
      config: {},
      
      actions: {
        spawnParticles: (_preset: string) => { /* TODO: Spawn particles */ },
        playSound: (_id: string) => { /* TODO: Play sound */ },
        setState: (_state: string) => { /* TODO: Set state */ },
      },
      
      services: this.services,
    };
  }
  
  private updateDetection(npc: NPCController, playerDistance: number): void {
    const isNowDetected = playerDistance <= this.detectionRange;
    
    if (isNowDetected && !this.detectionState.wasPlayerDetected) {
      // Player just entered detection range
      if (this.behavior.onPlayerDetected) {
        const api = this.createDetectionAPI(npc, playerDistance);
        this.behavior.onPlayerDetected(api);
      }
    } else if (!isNowDetected && this.detectionState.wasPlayerDetected) {
      // Player just left detection range
      if (this.behavior.onPlayerLost) {
        const api = this.createDetectionAPI(npc, playerDistance);
        this.behavior.onPlayerLost(api);
      }
    }
    
    this.detectionState.wasPlayerDetected = isNowDetected;
    this.detectionState.lastPlayerDistance = playerDistance;
  }
  
  /**
   * Update player distance from external source (called by NPCSpawnManager)
   */
  setPlayerDistance(distance: number): void {
    this.detectionState.lastPlayerDistance = distance;
  }
  
  /**
   * Update player T position from external source (called by NPCSpawnManager)
   */
  setPlayerT(t: number): void {
    this.detectionState.lastPlayerT = t;
  }
}
